/**
 * sl-yphr.14: Generic Fullscreen Modal
 *
 * Renders the active FullscreenRegistry view inside a modal overlay.
 * Handles: ESC key, backdrop click, browser back button.
 * DRY: One modal implementation for all fullscreen view types.
 */

import { IconButton } from '@affine/component';
import { EditorService } from '@affine/core/modules/editor';
import { useI18n } from '@affine/i18n';
import { CloseIcon } from '@blocksuite/icons/rc';
import {
  useLiveData,
  useService,
  useServiceOptional,
} from '@toeverything/infra';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import {
  hasActiveDatabaseCellEditor,
  isEditableKeyboardEventTarget,
} from '../../../blocksuite/utils/keyboard-event-target';
import { CachedUserAvatar } from '../../../components/cached-user-avatar';
import { VIEW_COVERING_LAYER_ATTRIBUTE } from '../copilot/context-picking-attributes';
import { FullscreenRegistry } from '../services/fullscreen-registry';
import * as styles from './fullscreen-modal.css';

interface FullscreenPresenceBadge {
  avatar?: string | null;
  color: string;
  id: number;
  name: string;
  source: 'canvas' | 'fullscreen';
  userId?: string | null;
}

function getPresenceInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '?';
  const initials = trimmed
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('');
  return initials || trimmed[0]?.toUpperCase() || '?';
}

const DatabaseFullscreenPresenceBadges: FC<{ databaseId: string }> = ({
  databaseId,
}) => {
  const editorService = useServiceOptional(EditorService);
  const page = editorService?.editor.doc.blockSuiteDoc;
  const [badges, setBadges] = useState<FullscreenPresenceBadge[]>([]);

  useEffect(() => {
    if (!page) {
      setBadges([]);
      return;
    }

    const awarenessStore = page.awarenessStore;
    const awareness = awarenessStore.awareness;
    const localClientId = awareness.clientID;
    const scope = `database:${page.id}:fullscreen`;
    const hasCanvasDatabaseSelection = (selectionV2: unknown): boolean => {
      if (!selectionV2 || typeof selectionV2 !== 'object') return false;
      return Object.values(selectionV2).some(selectionList => {
        if (!Array.isArray(selectionList)) return false;
        return selectionList.some(selection => {
          if (!selection || typeof selection !== 'object') return false;
          const record = selection as {
            elements?: unknown;
            type?: unknown;
          };
          return (
            record.type === 'surface' &&
            Array.isArray(record.elements) &&
            record.elements.includes(databaseId)
          );
        });
      });
    };
    const collectBadges = () => {
      const next: FullscreenPresenceBadge[] = [];
      awarenessStore.getStates().forEach((state, id) => {
        if (id === localClientId) return;
        const isInCanvas = hasCanvasDatabaseSelection(state.selectionV2);
        const presence = state.databaseFullscreenPresence;
        const isInFullscreen =
          presence?.scope === scope && presence.databaseId === databaseId;
        if (!isInCanvas && !isInFullscreen) {
          return;
        }
        next.push({
          id,
          name: state.user?.name ?? 'Unknown',
          avatar: state.user?.avatar,
          color: state.color ?? 'var(--streamlain-multi-players-blue)',
          source: isInFullscreen ? 'fullscreen' : 'canvas',
          userId: state.user?.id,
        });
      });
      next.sort((a, b) => a.id - b.id);
      setBadges(next);
    };

    collectBadges();
    awareness.on('change', collectBadges);
    return () => awareness.off('change', collectBadges);
  }, [databaseId, page]);

  if (badges.length === 0) return null;

  return (
    <div className={styles.presenceBadges}>
      {badges.map(badge => (
        <div
          key={badge.id}
          className={styles.presenceAvatar}
          title={
            badge.source === 'fullscreen'
              ? `${badge.name} is in fullscreen`
              : `${badge.name} selected this database on canvas`
          }
        >
          <div
            className={styles.presenceAvatarContent}
            style={{ backgroundColor: badge.color }}
          >
            {badge.avatar ? (
              <CachedUserAvatar
                userId={badge.userId}
                url={badge.avatar}
                name={badge.name}
                size={36}
              />
            ) : (
              <span>{getPresenceInitials(badge.name)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export const FullscreenModal: FC = () => {
  const t = useI18n();
  const registry = useService(FullscreenRegistry);
  const activeView = useLiveData(registry.activeView$);
  const views = useLiveData(registry.registeredViews$);
  const historyPushedRef = useRef(false);

  const config = activeView
    ? views.find(v => v.id === activeView.viewId)
    : null;

  const handleClose = useCallback(() => {
    registry.closeByUser();
  }, [registry]);

  // ESC key handler
  useEffect(() => {
    if (!activeView) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditableKeyboardEventTarget(e)) return;
      if (e.key === 'Escape') {
        // Popup-based editors (for example date) portal their controls outside
        // the cell. Let the database consume the first Escape and leave
        // fullscreen only after cell editing has ended.
        if (activeView.viewId === 'database' && hasActiveDatabaseCellEditor()) {
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleClose();
      }
    };
    window.addEventListener('keydown', onKeyDown, { capture: true });
    return () =>
      window.removeEventListener('keydown', onKeyDown, { capture: true });
  }, [activeView, handleClose]);

  // Browser back button: push history state when opening, pop to close
  useEffect(() => {
    if (!activeView) {
      historyPushedRef.current = false;
      return;
    }

    if (!historyPushedRef.current) {
      const stateKey = `fullscreen-${activeView.viewId}`;
      window.history.pushState({ [stateKey]: true }, '');
      historyPushedRef.current = true;
    }

    const onPopState = () => {
      if (registry.activeView$.value) {
        registry.close();
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [activeView, registry]);

  if (!activeView || !config) return null;

  const Component = config.component;
  const closeLabel = t['com.streamlain.streamlain.common.close']();

  return (
    <div
      className={styles.overlay}
      onClick={e => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className={styles.content}
        data-fullscreen-view={activeView.viewId}
        {...{ [VIEW_COVERING_LAYER_ATTRIBUTE]: '' }}
      >
        {activeView.viewId === 'database' ? (
          <DatabaseFullscreenPresenceBadges databaseId={activeView.blockId} />
        ) : null}
        {config.hideModalClose ? null : (
          <IconButton
            type="button"
            icon={<CloseIcon />}
            size="20"
            className={styles.closeButton}
            onClick={handleClose}
            aria-label={closeLabel}
            title={`${closeLabel} (ESC)`}
            withoutHover
          />
        )}
        <Component
          blockId={activeView.blockId}
          payload={activeView.payload}
          onClose={handleClose}
        />
      </div>
    </div>
  );
};
